/**
 * Shape-conversion between the rewrite's `Message` (core/types.ts) and the
 * `CachedMessage` shape IDB stores.
 *
 * The rewrite uses one `Message` type everywhere (network fetch, WS broadcast,
 * optimistic state), so unlike v1 there is no API-vs-SSE divergence — a single
 * `toCached` / `fromCached` pair round-trips it. Defaults are filled here so a
 * cache row hydrates back into a fully-valid `Message`.
 */

import type { Message, WorkSummary } from "../core/types.js";
import { normalizeReactions } from "../components/message/reactions/reactions.js";
import {
  messageAttachments,
  messageCard,
  messageMentions,
  messageText,
} from "../core/message-text.js";
import { type CachedMessage, createdAtKey } from "./idb.js";

// The agent-face fields: participants as sent, cyboId only when the relay stamped one.
function threadAvatarFields(
  m: Pick<Message, "threadParticipants" | "cyboId">,
): Pick<CachedMessage, "threadParticipants" | "cyboId"> {
  const tp = m.threadParticipants ?? null;
  return m.cyboId ? { threadParticipants: tp, cyboId: m.cyboId } : { threadParticipants: tp };
}

/** Convert a rewrite `Message` into a `CachedMessage` for one scope. */
/** The turn-end summary rides along only on the posts that have one. */
const workFields = (m: Message): { workSummary?: WorkSummary } =>
  m.workSummary ? { workSummary: m.workSummary } : {};

export function toCached(scopeKey: string, m: Message): CachedMessage {
  const createdAt = typeof m.createdAt === "number" ? m.createdAt : Date.now();
  return {
    id: String(m.id),
    scopeKey,
    workspaceId: m.workspaceId ?? null,
    channelId: m.channelId ?? null,
    fromId: m.fromId,
    fromType: m.fromType,
    fromName: m.fromName ?? null,
    toId: m.toId ?? null,
    text: m.text ?? "",
    mentions: m.mentions ?? null,
    parentId: m.parentId ?? null,
    attachments: (m.attachments as unknown[] | null) ?? null,
    unfurls: (m.unfurls as unknown[] | null) ?? null,
    card: m.card ?? null,
    ...workFields(m),
    reactions: (m.reactions ?? []) as CachedMessage["reactions"],
    pinnedAt: m.pinnedAt ?? null,
    pinnedBy: m.pinnedBy ?? null,
    replyCount: m.replyCount ?? 0,
    lastReplyAt: m.lastReplyAt ?? null,
    ...threadAvatarFields(m),
    ...(m.source ? { source: m.source } : {}),
    seq: typeof m.seq === "number" ? m.seq : 0,
    createdAt,
    updatedAt: m.updatedAt ?? null,
    createdAtKey: createdAtKey(createdAt),
    cachedAt: Date.now(),
    deleted: m.deleted === true,
  };
}

/** Rebuild a rewrite `Message` from a cached row (for cold render). */
export function fromCached(m: CachedMessage): Message {
  return {
    id: m.id,
    workspaceId: m.workspaceId ?? undefined,
    channelId: m.channelId,
    fromId: m.fromId,
    fromType: m.fromType,
    fromName: m.fromName ?? undefined,
    toId: m.toId,
    text: messageText(m.text),
    mentions: messageMentions(m.mentions),
    parentId: m.parentId,
    attachments: messageAttachments(m.attachments),
    unfurls: (m.unfurls as Message["unfurls"]) ?? null,
    card: messageCard(m.card),
    ...(m.workSummary ? { workSummary: m.workSummary as WorkSummary } : {}),
    reactions: normalizeReactions(m.reactions),
    pinnedAt: m.pinnedAt,
    pinnedBy: m.pinnedBy,
    replyCount: m.replyCount,
    lastReplyAt: m.lastReplyAt,
    threadParticipants: m.threadParticipants,
    ...(m.cyboId ? { cyboId: m.cyboId } : {}),
    ...(m.source ? { source: m.source } : {}),
    updatedAt: m.updatedAt,
    deleted: m.deleted || undefined,
    seq: m.seq,
    createdAt: m.createdAt,
  };
}
